<template>
  <LoadingComponent v-if="loading" />
  <template v-if="!loading && localDashboard">
    <div class="shrink-0">
      <DashboardHeader :dashboard="localDashboard">
        <template #cta>
          <DashboardPanelControl
            ref="panel_control"
            v-model:search-value="searchValue"
            v-model:dashboard="localDashboard"
            v-model:require-save-dashboard="requireSaveDashboard"
            :count-filter="validFilter.length"
            :is-dirty="$isStateDirty('localDashboard.filters')"
            :need-save-locked="loading"
            @clear-filters="clearFilters"
            @export-file="handleExportFile"
          >
            <template #filter-content="{ close }">
              <FilterLinesComponent
                v-model:filters="localDashboard.filters"
                v-model:require-save-dashboard="requireSaveDashboard"
                :filters-available="availableFilter"
                :dashboard-type="localDashboard.type"
                @close-popover="() => closePopover(close)"
              />
            </template>
            <template #other-cta>
              <div class="flex flex-row gap-2">
                <BaseButton
                  v-if="canCreateNote"
                  size="small"
                  icon="plus"
                  :title="$t('button.create_commission_note')"
                  @click="createModalOpen = true"
                />
                <AddNewColumnComponent
                  model="commission_note"
                  @add-column="addColumn"
                />
              </div>
            </template>
          </DashboardPanelControl>
        </template>
      </DashboardHeader>
    </div>
    <div class="relative flex min-h-0 flex-1 flex-col gap-4 overflow-auto">
      <!-- a list already shown is covered while it is reloaded; an empty one shows its own spinner -->
      <div
        v-if="loadingList && filteredCommissionNotes.length"
        data-test="reload-overlay"
        class="absolute top-1/2 left-1/2 z-100 h-36 w-36 -translate-x-1/2 -translate-y-1/2 transform rounded-2xl bg-gray-200 dark:bg-slate-700"
      >
        <LoadingComponent :display-label="false" />
      </div>
      <CommissionNoteTable
        v-model:columns="localDashboard.columns"
        v-model:sort="localDashboard.sort"
        v-model:require-save-dashboard="requireSaveDashboard"
        :commission-notes="filteredCommissionNotes"
        :loading="loadingList"
        @update:dashboard="updateDashboard"
      />
    </div>
  </template>
  <CommissionNoteCreateSlideOver
    v-if="createModalOpen"
    v-model:open="createModalOpen"
    @created="openCreatedNote"
    @close="createModalOpen = false"
  />
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import BaseButton from '@c/addf-package/components/BaseButton/BaseButton.vue'
import CommissionNoteCreateSlideOver from '@c/Dashboard/Content/CommissionNote/CommissionNoteCreateSlideOver.vue'
import LoadingComponent from '@c/TheLoadingComponent.vue'
import DashboardHeader from '@c/Dashboard/Components/DashboardHeader.vue'
import DashboardPanelControl from '@c/Dashboard/Components/DashboardPanelControl.vue'
import FilterLinesComponent from '@c/Dashboard/Components/FilterLinesComponent.vue'
import AddNewColumnComponent from '@c/Dashboard/Content/Invoicing/components/Head/columnSettings/AddNewColumnComponent.vue'
import CommissionNoteTable from '@c/Dashboard/Content/CommissionNote/CommissionNoteTable.vue'
import {
  rowMatchesSearch,
  searchableKeys
} from '@c/Dashboard/Components/dashboardSearch'
import {
  compareKeys,
  sortKey
} from '@c/Dashboard/Content/CommissionNote/commissionNoteSort'
import persistViewFiltersStateMixin from '@/mixins/persistViewFiltersStateMixin'

export default {
  name: 'CommissionNoteContent',
  components: {
    BaseButton,
    CommissionNoteCreateSlideOver,
    CommissionNoteTable,
    AddNewColumnComponent,
    FilterLinesComponent,
    DashboardPanelControl,
    DashboardHeader,
    LoadingComponent
  },
  mixins: [persistViewFiltersStateMixin],
  persistViewState: ['searchValue', 'localDashboard.filters'],
  props: {
    currentBoard: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      loading: false,
      loadingList: false,
      listRequest: 0,
      exporting: false,
      searchValue: '',
      commissionNoteList: [],
      localDashboard: null,
      requireSaveDashboard: false,
      createModalOpen: false
    }
  },
  computed: {
    ...mapGetters({
      availableFilter: 'metadata/getAvailableFilterCommissionNote'
    }),
    canCreateNote() {
      return (
        this.$can('manage_commission_notes') ||
        this.$can('manage_commission_settings')
      )
    },
    validFilter() {
      return (
        this.localDashboard.filters?.filter(
          (filter) => filter.column && filter.values && filter.condition
        ) ?? []
      )
    },
    filteredCommissionNotes() {
      let list = [...this.commissionNoteList]
      if (this.searchValue.length >= 2) {
        const searchValue = this.$h.normalizeSearch(this.searchValue)
        const keys = searchableKeys(this.localDashboard.columns)
        list = list.filter((model) =>
          rowMatchesSearch(model, keys, searchValue, (value) =>
            this.$h.normalizeSearch(value)
          )
        )
      }
      const sort = this.localDashboard.sort
      const column = this.localDashboard.columns.find(
        (column) => column.id === sort?.column_id
      )
      const field = column?.sort?.value ?? column?.field
      if (field) {
        // each row is read once, then sorted on what it shows: amounts as numbers, days as days
        list = list
          .map((row) => ({ row, key: sortKey(row[field], column.type) }))
          .sort((a, b) => compareKeys(a.key, b.key))
          .map(({ row }) => row)
        if (sort.direction === 'desc') {
          list.reverse()
        }
      }
      return list
    }
  },
  watch: {
    currentBoard(newValue, oldValue) {
      if (newValue !== oldValue) {
        this.localDashboard = newValue
        this.fetchTemplateDashboardConfig()
      }
    }
  },
  created() {
    this.fetchTemplateDashboardConfig()
  },
  methods: {
    ...mapActions({
      processFetchCommissionNoteListAction:
        'commissionNote/fetchCommissionNoteList',
      processFetchTemplateDashboardByIdAction:
        'dashboard/fetchTemplateDashboardById',
      processExportCommissionNoteListAction:
        'commissionNote/exportCommissionNoteList',
      processPdfCommissionNoteListAction: 'commissionNote/pdfCommissionNoteList'
    }),
    clearFilters() {
      this.localDashboard.filters = this.$getInitialState(
        'localDashboard.filters'
      )
      this.fetchCommissionNotes()
    },
    fetchTemplateDashboardConfig() {
      this.loading = true
      const targetId = this.currentBoard.id
      this.processFetchTemplateDashboardByIdAction({ id: targetId })
        .then((response) => {
          if (this.currentBoard.id !== targetId) return
          this.localDashboard = response.template_dashboard
          this.$restoreCachedState(
            'localDashboard.filters',
            response.template_dashboard.filters
          )
          this.fetchCommissionNotes()
        })
        .catch((error) => {
          if (
            this.currentBoard.id === targetId &&
            [403, 404].includes(error?.response?.status)
          ) {
            this.$router.push({ name: 'dashboard_main_page_route' })
          }
        })
        .finally(() => {
          if (this.currentBoard.id === targetId) this.loading = false
        })
    },
    updateDashboard(reload) {
      this.$refs.panel_control.saveDashboard(() => {
        if (reload) {
          this.fetchCommissionNotes()
        }
      })
    },
    fetchCommissionNotes() {
      this.loadingList = true
      // only the answer of the last request is kept
      const request = (this.listRequest += 1)
      this.processFetchCommissionNoteListAction({
        filters: this.localDashboard.filters,
        columns: this.localDashboard.columns,
        template_id: this.localDashboard.id
      })
        .then((response) => {
          if (request !== this.listRequest) return
          this.commissionNoteList = response.commission_notes
        })
        .catch(() => {})
        .finally(() => {
          if (request === this.listRequest) this.loadingList = false
        })
    },
    closePopover(close) {
      if (typeof close === 'function') {
        close()
      }
      this.fetchCommissionNotes()
    },
    openCreatedNote({ commission_note, created }) {
      if (!created) {
        this.$h.toastWarning(this.$t('global.commission_note_existing_opened'))
      }
      this.$router.push({
        name: 'commission_note_show_route',
        params: { id: commission_note.id }
      })
    },
    handleExportFile(format) {
      // a second click while the file is being built would start a second export
      if (this.exporting) {
        return
      }

      this.exporting = true
      // The API builds the file from the filters, columns, sort and search of the list
      const action =
        format === 'pdf'
          ? this.processPdfCommissionNoteListAction
          : this.processExportCommissionNoteListAction
      const sortColumn = this.localDashboard.columns.find(
        (column) => column.id === this.localDashboard.sort?.column_id
      )

      action({
        filters: this.localDashboard.filters,
        columns: this.localDashboard.columns,
        template_id: this.localDashboard.id,
        sort: sortColumn
          ? {
              column: sortColumn.field,
              dir: this.localDashboard.sort.direction
            }
          : undefined,
        // same threshold as the search of the list
        terms: this.searchValue.length >= 2 ? this.searchValue : undefined,
        // Excel is the default of the export route, CSV is asked with its format
        ...(format === 'csv' ? { format: 'csv' } : {})
      })
        .then((response) => {
          this.openFile(response.url)
        })
        // a failed request is already shown by the API client
        .catch(() => {})
        .finally(() => {
          this.exporting = false
        })
    },
    // the file is built by an asynchronous call, so a pop-up blocker may refuse its window:
    // it is then offered in a message, and the click on it opens the file
    openFile(url) {
      if (window.open(url)) {
        return
      }

      this.$h.toastAction('info', this.$t('global.commission_notes'), null, [
        { title: this.$t('button.download'), action: () => window.open(url) }
      ])
    },
    addColumn(config) {
      this.localDashboard.columns.push({
        ...config,
        sum_able: false,
        template_id: this.localDashboard.id
      })
    }
  }
}
</script>
